import {
  Address,
  AddressMeterPointDetails,
  ChangeOfTenancyMoveInRequest,
  DirectDebitInfo,
  MeterRegisterRateName,
  MoveInMeterReading,
  OccupantContact,
} from "@/api/model"
import { getCookie } from "@/lib/shared/useCookies.ts"
import { MeterpointReadings } from "@/types/MeterpointReading"
import { ref } from "vue"

export type CotMoveInRequest = Omit<
  ChangeOfTenancyMoveInRequest,
  | "billingAccountId"
  | "directDebitInfo"
  | "newOccupantContact"
  | "billingAddress"
  | "supplyAddress"
  | "meterpointReadings"
> & {
  billingAccountId?: number
}

// Extra information we use in the ui but it's not needed it in the move-in payload
export type CotMoveInExtras = {
  privacyAuthorisation: boolean
  directDebitAuthorisation: boolean
  candidateFDOR?: string
  fullAddress: string
  meterpointIdentifiers: string[]
}

const cotMoveInExtras = ref<CotMoveInExtras>({
  privacyAuthorisation: false,
  directDebitAuthorisation: false,
  candidateFDOR: undefined,
  fullAddress: "",
  meterpointIdentifiers: [],
})

const cotMoveInRequest = ref<CotMoveInRequest>({
  billingAccountId: undefined,
  firstDateOfResponsibility: "",
})

const newOccupantContact = ref<OccupantContact>({
  dateOfBirth: "",
  email: "",
  firstName: "",
  lastName: "",
  marketingOptIn: false,
  phone1: "",
  title: undefined,
})

const moveInAddress = ref<Address>({
  addressLine1: "",
  addressLine2: undefined,
  addressLine3: undefined,
  countryCode: "",
  postcode: "",
})

const directDebitInfo = ref<DirectDebitInfo>({
  accountName: "",
  accountNumber: "",
  paymentAmount: 100,
  paymentDayOfMonth: 0,
  sortCode: "",
})

const meterReadings = ref<MoveInMeterReading[]>([])

const crossFlowAddress = ref<AddressMeterPointDetails | null>(null)

const fetchMoveInAddressFromCookie = async (): Promise<
  AddressMeterPointDetails | undefined
> => {
  const moveInAddress = await getCookie("move_in_address")
  if (!moveInAddress) return undefined
  return {
    address: {
      addressLine1: moveInAddress.addressLine1,
      addressLine2: moveInAddress.addressLine2,
      addressLine3: moveInAddress.addressLine3,
      countryCode: moveInAddress.countryCode,
      postcode: moveInAddress.postcode,
    },
    billingAccountId: moveInAddress.billingAccountId,
    candidateFDOR: moveInAddress.candidateFDOR,
    fullAddress: moveInAddress.fullAddress,
    mpan: moveInAddress.mpan,
    mprn: moveInAddress.mprn,
  }
}

export const useCotMoveIn = () => {
  const updateMoveInRequest = (data: Partial<CotMoveInRequest>) => {
    cotMoveInRequest.value = { ...cotMoveInRequest.value, ...data }
  }

  const updateAddress = (data: AddressMeterPointDetails) => {
    cotMoveInRequest.value = {
      ...cotMoveInRequest.value,
      billingAccountId: data.billingAccountId,
    }
    moveInAddress.value = data.address
    cotMoveInExtras.value = {
      ...cotMoveInExtras.value,
      candidateFDOR: data.candidateFDOR,
      fullAddress: data.fullAddress ?? "",
      meterpointIdentifiers: [data.mpan, data.mprn].filter(Boolean) as string[],
    }
  }

  const updateNewOccupantContact = (data: OccupantContact) => {
    newOccupantContact.value = data
  }

  const updateDirectDebitInfo = (data: Partial<DirectDebitInfo>) => {
    directDebitInfo.value = {
      ...directDebitInfo.value,
      ...data,
    }
  }

  const updateMoveInExtras = (data: Partial<CotMoveInExtras>) => {
    cotMoveInExtras.value = { ...cotMoveInExtras.value, ...data }
  }

  const updateMeterReading = (data: MeterpointReadings[]) => {
    meterReadings.value = data.flatMap((meter) =>
      meter.readings.map((reading) => ({
        meterPointId: meter.meterpointId,
        meterIdentifier: meter.meterIdentifier,
        meterReading: Number.parseInt(reading.reading.join(""), 10),
        rateName: reading.rateName ?? MeterRegisterRateName.standard,
        readingDate: meter.readingDate,
        registerIdentifier: reading.registerIdentifier,
        type: meter.type,
      })),
    )
  }

  const getCrossFlowAddress = (): AddressMeterPointDetails | null => {
    return crossFlowAddress.value
  }

  const setCrossFlowAddress = (data: AddressMeterPointDetails) => {
    crossFlowAddress.value = data
  }

  const clearCrossFlowAddress = () => {
    crossFlowAddress.value = null
  }

  return {
    cotMoveInRequest,
    newOccupantContact,
    moveInAddress,
    directDebitInfo,
    cotMoveInExtras,
    meterReadings,
    getCrossFlowAddress,
    updateMoveInRequest,
    updateAddress,
    updateMoveInExtras,
    updateNewOccupantContact,
    updateDirectDebitInfo,
    updateMeterReading,
    fetchMoveInAddressFromCookie,
    setCrossFlowAddress,
    clearCrossFlowAddress,
  }
}
